import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Shield, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'editor' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/auth/users');
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/auth/users', form);
      toast.success('Administrador creado');
      setDialog(false);
      setForm({ username: '', password: '', role: 'editor' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar administrador?')) return;
    try {
      await api.delete(`/auth/users/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Administradores</h1>
          <p className="text-slate-500 text-sm">Gestiona accesos al panel</p>
        </div>
        <Button onClick={() => setDialog(true)} className="rounded-full btn-pink" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuevo usuario
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Usuario</th>
              <th className="text-left p-3">Rol</th>
              <th className="text-left p-3">Creado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="p-3 flex items-center gap-2">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center ${u.role === 'admin' ? 'bg-pinky-100 text-pinky-600' : 'bg-slate-100 text-slate-600'}`}>
                    {u.role === 'admin' ? <Shield className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                  </div>
                  <span className="font-semibold">{u.username}</span>
                </td>
                <td className="p-3 capitalize">{u.role}</td>
                <td className="p-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right">
                  {me?.id !== u.id && (
                    <button onClick={() => remove(u.id)} className="text-rose-600 hover:bg-rose-50 h-8 w-8 rounded-lg inline-flex items-center justify-center" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo administrador</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Usuario</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl" data-testid="user-username-input" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl" data-testid="user-password-input" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-xl" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.username || !form.password} className="rounded-full btn-pink" data-testid="user-save-btn">
              {saving ? 'Creando…' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
